/**
 * PasswordLoginForm — innlogging med familiekode, brukernavn og passord
 * (#141, FF-09 #177).
 *
 * POST /api/auth/login. Øktlengden avgjøres av serveren (#142): på en
 * innrullert kjøkkenskjerm blir økten 'tablet' og bundet til enheten, ellers
 * en vanlig personlig økt.
 *
 * Familiekoden (FF-09): feltet vises ikke på en innrullert kjøkkenskjerm —
 * den vet familien. Ellers fylles det med `?familie=` eller koden enheten
 * husker; en husket kode vises som «Familie: kode» med «Bytt familie». Feltet
 * kan stå tomt: serveren bruker da den eneste familien på instansen, så
 * familien som fantes før familiekodene, logger inn som før. Etter en
 * vellykket innlogging huskes koden (eller glemmes, når den var tom).
 *
 * Feil (kontrakt fra #140):
 *   - 401 invalid_credentials        → «Feil familiekode, brukernavn eller passord»
 *   - 403 family_suspended           → familien er stengt
 *   - 401 locked + Retry-After       → nedtelling via usePinLockout
 *   - 429 rate_limited + Retry-After → samme nedtelling
 *   - nettverksfeil                  → egen melding
 *
 * Passordet tømmes etter hvert forsøk, vellykket eller ikke. Brukernavnet
 * valideres bevisst ikke mot mønsteret (se LoginBody): et ugyldig brukernavn
 * skal gi samme svar som et ukjent.
 *
 * Utseende (#159, steg 9g, Stell_Innlogging): låsingen er et amber banner
 * øverst i skjemaet, rett under «Logg inn», og knappen er 56 px.
 */
import { useState } from 'react';
import type { ReactElement } from 'react';
import type { LoginResponse } from '@stell/shared';
import { useTranslation } from 'react-i18next';
import { Button, Icon } from '../../components/primitives';
import { usePinLockout } from '../../components/hooks/usePinLockout';
import { AuthApi, asApiError, authErrorMessage } from '../auth/authApi';
import { FamilyCodeField, PasswordField, UsernameField } from '../auth/AuthFields';
import { readRememberedFamilyCode, rememberFamilyCode } from './familyCode';

export interface PasswordLoginFormProps {
  /** Får svaret, så LoginRoute kan sende til /velg-passord (#143). */
  onSuccess: (res: LoginResponse) => void;
  /** Forhåndsutfylt brukernavn, f.eks. fra et roster-medlem uten PIN (#142). */
  initialUsername?: string;
  /** Vis familiekoden. Av på en innrullert kjøkkenskjerm, som vet familien (FF-09). */
  showFamily?: boolean;
  /** Familiekode fra lenken (`?familie=`); vinner over den enheten husker. */
  initialFamily?: string;
}

// Samme tekst for kort og lang låsing — «spør en voksen» passer ikke her.
const LOCKOUT_KEYS = { short: 'auth.error.locked', long: 'auth.error.locked' };

export function PasswordLoginForm({
  onSuccess,
  initialUsername = '',
  showFamily = true,
  initialFamily = '',
}: PasswordLoginFormProps): ReactElement {
  const { t } = useTranslation();
  const lockout = usePinLockout(LOCKOUT_KEYS);
  const [remembered] = useState(() => (initialFamily ? '' : readRememberedFamilyCode()));
  const [family, setFamily] = useState(initialFamily || remembered);
  // En husket kode vises som tekst med «Bytt familie»; en kode fra lenken i feltet.
  const [editingFamily, setEditingFamily] = useState(remembered === '');
  const [username, setUsername] = useState(initialUsername);
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const locked = lockout.secondsRemaining > 0;
  const canSubmit = !pending && !locked && username.trim() !== '' && password !== '';

  async function submit(): Promise<void> {
    if (!canSubmit) return;
    setPending(true);
    setError(null);
    try {
      const code = showFamily ? family.trim() : '';
      const res = await AuthApi.login({
        ...(code ? { family: code } : {}),
        username: username.trim(),
        password,
      });
      lockout.cancel();
      if (showFamily) rememberFamilyCode(code);
      onSuccess(res);
    } catch (err) {
      const e = asApiError(err);
      if (e.retryAfter) {
        // Nedtellingen viser selve meldingen og teller ned levende.
        lockout.startLockout(e.retryAfter);
      } else if (e.code === 'invalid_credentials' && showFamily) {
        setError(t('login.password.invalid_credentials_family'));
      } else {
        setError(authErrorMessage(t, e));
      }
    } finally {
      setPassword('');
      setPending(false);
    }
  }

  return (
    <form
      data-testid="password-login-form"
      className="flex w-full flex-col gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      {/* Live-regionen står alltid i DOM-en, så skjermlesere leser opp
          banneret når det dukker opp. Tom spiser den gap-en sin (-mb-4). */}
      <div role="status" aria-live="polite" aria-atomic="true" className="empty:-mb-4">
        {lockout.lockoutMessage ? (
          <p
            data-testid="password-login-lockout"
            className="m-0 flex items-center gap-2.5 rounded-md bg-warn-bg px-3.5 py-3 text-sm font-bold text-ink tnum"
          >
            <Icon.Clock size={20} className="shrink-0 text-warn" />
            {lockout.lockoutMessage}
          </p>
        ) : null}
      </div>
      {showFamily && !editingFamily ? (
        <p data-testid="login-family-remembered" className="m-0 flex flex-wrap items-baseline gap-x-2 text-control text-ink-2">
          <span>
            {t('login.password.family_remembered')}{' '}
            <strong className="font-bold text-ink">{family}</strong>
          </span>
          <button
            type="button"
            data-testid="login-family-change"
            className="min-h-11 px-1 text-sm font-bold text-ink underline"
            onClick={() => {
              setFamily('');
              setEditingFamily(true);
            }}
          >
            {t('login.password.family_change')}
          </button>
        </p>
      ) : null}
      {showFamily && editingFamily ? (
        <FamilyCodeField
          label={t('login.password.family')}
          hint={t('login.password.family_hint')}
          value={family}
          onChange={setFamily}
          testId="login-family"
          autoFocus={family === '' && initialUsername === ''}
        />
      ) : null}
      <UsernameField
        label={t('auth.username')}
        value={username}
        onChange={setUsername}
        testId="login-username"
        autoFocus={initialUsername === '' && !(showFamily && editingFamily && family === '')}
        validate={false}
      />
      <PasswordField
        label={t('auth.password')}
        value={password}
        onChange={setPassword}
        autoComplete="current-password"
        testId="login-password"
        autoFocus={initialUsername !== ''}
      />
      {error ? (
        <p data-testid="password-login-error" className="m-0 text-sm text-bad" role="alert">
          {error}
        </p>
      ) : null}
      <Button
        type="submit"
        size={56}
        disabled={!canSubmit}
        data-testid="password-login-submit"
        className="mt-1 w-full"
      >
        {pending ? t('login.password.submitting') : t('login.password.submit')}
      </Button>
    </form>
  );
}
